import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
} from '@mui/material';
import { BugReport } from '@mui/icons-material';
import { auth, db } from '../config/firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { SharedContentService } from '../services/sharedContentService';
import { StockService } from '../services/stockService';

const DebugSharing: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  const runTest = async (testName: string, testFn: () => Promise<any>) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log(`\n🧪 Running test: ${testName}`);
      const result = await testFn();
      setResults(prev => [...prev, { testName, success: true, result }]);
    } catch (err: any) {
      console.error(`❌ Test failed: ${testName}`, err);
      setResults(prev => [...prev, { testName, success: false, error: err.message }]);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const testDirectQuery = async () => {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    // Test 1: Query own watchlist
    console.log('Test 1: Querying own watchlist...');
    const ownQuery = query(
      collection(db, 'watchlist'),
      where('userId', '==', user.uid)
    );
    const ownSnapshot = await getDocs(ownQuery);
    console.log(`Own watchlist: ${ownSnapshot.size} entries`);

    // Test 2: Find a shared watchlist
    console.log('\nTest 2: Finding shared watchlists...');
    const sharedContent = await SharedContentService.getSharedContent('watchlist', 'recent', 5);
    console.log(`Found ${sharedContent.length} shared watchlists`);

    if (sharedContent.length === 0) {
      return { message: 'No shared watchlists found' };
    }

    // Test 3: Query another user's watchlist directly
    const otherUserId = sharedContent[0].userId;
    console.log(`\nTest 3: Querying watchlist for user ${otherUserId}...`);
    
    const otherQuery = query(
      collection(db, 'watchlist'),
      where('userId', '==', otherUserId),
      where('isShared', '==', true)
    );
    
    console.log('Executing query...');
    const otherSnapshot = await getDocs(otherQuery);
    console.log(`Other user's shared entries: ${otherSnapshot.size}`);

    // Test 4: Check a sample entry
    if (!otherSnapshot.empty) {
      const sampleDoc = otherSnapshot.docs[0];
      const sampleData = sampleDoc.data();
      console.log('\nSample entry:', {
        id: sampleDoc.id,
        symbol: sampleData.symbol,
        userId: sampleData.userId,
        isShared: sampleData.isShared,
        isActive: sampleData.isActive,
      });
    }

    return {
      ownEntries: ownSnapshot.size,
      sharedWatchlists: sharedContent.length,
      otherUserEntries: otherSnapshot.size,
      sampleUserId: otherUserId,
    };
  };

  const testStockService = async () => {
    const sharedContent = await SharedContentService.getSharedContent('watchlist', 'recent', 1);
    if (sharedContent.length === 0) {
      throw new Error('No shared watchlists found');
    }

    const userId = sharedContent[0].userId;
    console.log(`Testing StockService.getUserWatchlist for userId: ${userId}`);
    
    const entries = await StockService.getUserWatchlist(userId);
    console.log(`StockService returned ${entries.length} entries`);
    
    return {
      userId,
      entriesCount: entries.length,
      firstEntry: entries[0] || null,
    };
  };

  const testQueryPermutation = async () => {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    // Get a shared watchlist
    const sharedContent = await SharedContentService.getSharedContent('watchlist', 'recent', 1);
    if (sharedContent.length === 0) {
      throw new Error('No shared watchlists found');
    }

    const targetUserId = sharedContent[0].userId;
    console.log('Testing different query permutations for userId:', targetUserId);

    // Test 1: Just userId
    console.log('\nTest 1: Just userId...');
    try {
      const q1 = query(
        collection(db, 'watchlist'),
        where('userId', '==', targetUserId)
      );
      const s1 = await getDocs(q1);
      console.log('✅ Success! Found', s1.size, 'documents');
    } catch (e: any) {
      console.error('❌ Failed:', e.code, e.message);
    }

    // Test 2: userId + isShared in one query
    console.log('\nTest 2: userId + isShared in one query...');
    try {
      const q2 = query(
        collection(db, 'watchlist'),
        where('userId', '==', targetUserId),
        where('isShared', '==', true)
      );
      const s2 = await getDocs(q2);
      console.log('✅ Success! Found', s2.size, 'documents');
    } catch (e: any) {
      console.error('❌ Failed:', e.code, e.message);
    }

    // Test 3: Get all docs and filter in memory
    console.log('\nTest 3: Get all and filter in memory...');
    try {
      const q3 = query(
        collection(db, 'watchlist'),
        where('userId', '==', targetUserId)
      );
      const s3 = await getDocs(q3);
      const sharedDocs = s3.docs.filter(doc => doc.data().isShared === true);
      console.log('✅ Found', s3.size, 'total,', sharedDocs.length, 'shared');
    } catch (e: any) {
      console.error('❌ Failed:', e.code, e.message);
    }

    // Test 4: Try to get a specific document by ID
    console.log('\nTest 4: Get specific document by ID...');
    try {
      const q4 = query(
        collection(db, 'watchlist'),
        where('userId', '==', targetUserId)
      );
      const s4 = await getDocs(q4);
      if (s4.size > 0) {
        const firstDocId = s4.docs[0].id;
        console.log('Trying to get document:', firstDocId);
        const docRef = doc(db, 'watchlist', firstDocId);
        const docSnap = await getDoc(docRef);
        console.log('✅ Direct doc access:', docSnap.exists() ? 'Success' : 'Failed');
        if (docSnap.exists()) {
          console.log('Document data:', {
            symbol: docSnap.data().symbol,
            userId: docSnap.data().userId,
            isShared: docSnap.data().isShared
          });
        }
      }
    } catch (e: any) {
      console.error('❌ Failed:', e.code, e.message);
    }

    return {
      targetUserId,
      testsCompleted: 4
    };
  };

  const runAllTests = async () => {
    setResults([]);
    
    await runTest('Direct Firestore Query', testDirectQuery);
    await runTest('Query Permutation Test', testQueryPermutation);
    await runTest('StockService.getUserWatchlist', testStockService);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Debug Sharing Permissions
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="body1" paragraph>
          This page tests the watchlist sharing functionality to identify permission issues.
        </Typography>
        
        <Button
          variant="contained"
          onClick={runAllTests}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <BugReport />}
        >
          {loading ? 'Running Tests...' : 'Run Debug Tests'}
        </Button>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {results.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Test Results
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Test</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Result</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((result, index) => (
                  <TableRow key={index}>
                    <TableCell>{result.testName}</TableCell>
                    <TableCell>
                      <Typography color={result.success ? 'success.main' : 'error.main'}>
                        {result.success ? '✅ Success' : '❌ Failed'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {result.success ? (
                        <pre>{JSON.stringify(result.result, null, 2)}</pre>
                      ) : (
                        <Typography color="error">{result.error}</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Card sx={{ mt: 3, bgcolor: 'grey.100' }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Check the browser console for detailed debug output
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DebugSharing;